import { groupLifeProposalsService } from '@/services/proposals/group-life';
import type { GroupLifeCloneProposalResponse } from '@/services/proposals/group-life';
import { toaster } from '@/shared/ui/toaster.component';
import {
  clearCloneIdempotencyKey,
  getCloneIdempotencyKey,
  GroupLifeClonePendingError,
  isClonePendingStatus,
} from '@/shared/utils/group-life-clone-idempotency.util';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type Variables = {
  policyId: string;
};

/**
 * Cancela a proposta PME e clona uma nova. O recálculo das vidas segue em
 * segundo plano; o aviso vem no `warning` da resposta.
 */
export function useAlterGroupLifeProposal() {
  const queryClient = useQueryClient();

  return useMutation<GroupLifeCloneProposalResponse, Error, Variables>({
    mutationKey: ['group-life-proposal', 'alter'],
    mutationFn: async ({ policyId }) => {
      const response = await groupLifeProposalsService.alterProposal(
        policyId,
        getCloneIdempotencyKey(policyId, 'ALTER')
      );
      if (!response.success || !response.data) {
        if (isClonePendingStatus(response.status)) {
          throw new GroupLifeClonePendingError('ALTER');
        }
        throw new Error(response.error || 'Erro ao alterar proposta');
      }
      clearCloneIdempotencyKey(policyId, 'ALTER');
      return response.data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['proposal-analyses'] });
      if (data.warning) {
        toaster.create({
          type: 'info',
          title: 'Recálculo em andamento',
          description: data.warning,
        });
      }
    },
    onError: error => {
      if (error instanceof GroupLifeClonePendingError) {
        queryClient.invalidateQueries({ queryKey: ['proposals'] });
        queryClient.invalidateQueries({ queryKey: ['proposal-analyses'] });
      }
    },
  });
}
